import React from "react";
import moment from 'moment';
import 'moment/locale/fr';
import { Calendar, momentLocalizer } from 'react-big-calendar';

import Loader from "../Layout/Components/Loader";
import Event from "../Layout/Components/Modal/Event";
import Booking from "../Layout/Components/Modal/Booking";
import Intervention from "../Layout/Components/Modal/Intervention";

import UserContext from "../userContext";

import 'react-calendar/dist/Calendar.css';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../styles/main.scss';

import { classNameBlocking, titleName } from "../utils/namings-classnames";

export default function Dashboard() {
    const userContext = React.useContext(UserContext);
    const [activeHouseId, setActiveHouseId] = React.useState(null);
    const [sE, setSE] = React.useState({});
    const [loading, setLoading] = React.useState(false);
    const [optimisticBookings, setOptimisticBookings] = React.useState({});
    const [monthCursorByHouse, setMonthCursorByHouse] = React.useState({});
    const visibleHouses = (userContext.data.houses || []).filter((house) => !house?.archived);
    const visibleHouseIds = visibleHouses.map((h) => h._id).join('|');

    const resolvedActiveHouseId = React.useMemo(() => {
        if (!visibleHouses.length) return null;
        if (activeHouseId && visibleHouses.some((h) => h._id === activeHouseId)) return activeHouseId;
        return visibleHouses[0]._id;
    }, [visibleHouses, activeHouseId]);

    React.useEffect(() => {
        if (!visibleHouses.length) return;
        setActiveHouseId((current) => {
            if (current && visibleHouses.some((h) => h._id === current)) return current;
            return visibleHouses[0]._id;
        });
    }, [visibleHouseIds]);

    const handleRefresh = () => {
        setLoading(true);
        userContext.apiReducer('fetchData')
            .then(() => setLoading(false))
            .catch(() => setLoading(false));
    };

    const handleBookingCreated = (houseId, booking) => {
        setOptimisticBookings((prev) => ({
            ...prev,
            [houseId]: [...(prev[houseId] || []), booking]
        }));
        setSE({});
    };

    return (
        <div className="dashboard">
            <Loader show={loading} message={'Chargement en cours...'} />

            {/* Main Content */}
            <main className="dashboard-main">
                <div className="page-title page-title--with-action">
                    <div>
                        <h2>Planning</h2>
                        <p>Consultez les réservations de vos propriétés</p>
                    </div>
                    <button className="refresh-btn" onClick={handleRefresh}>
                        <i className="fas fa-sync-alt" />
                        <span className="btn-text">Actualiser</span>
                    </button>
                </div>

                {/* Property Tabs */}
                {visibleHouses.length > 0 ? (
                    <div className="property-tabs">
                        <nav className="tabs" role="tablist">
                            {visibleHouses.map((house) => (
                                <button
                                    key={`tab-${house._id}`}
                                    type="button"
                                    className={`tab-item ${house._id === resolvedActiveHouseId ? 'active' : ''}`}
                                    role="tab"
                                    aria-selected={house._id === resolvedActiveHouseId}
                                    onClick={() => {
                                        setSE({});
                                        setActiveHouseId(house._id);
                                    }}
                                    data-tab={house._id}
                                >
                                    {house.name}
                                </button>
                            ))}
                        </nav>
                    </div>
                ) : null}

                {/* Property Panels */}
                {visibleHouses.length > 0 ? (
                    visibleHouses.map((house) => {
                        const localizer = momentLocalizer(moment);
                        const eventsList = buildEventsList(house, optimisticBookings[house._id] || []);
                        const currentMonthDate = monthCursorByHouse[house._id] || new Date();
                        const calendarHeight = getCalendarHeight(eventsList, currentMonthDate);
                        const legendItems = getVisibleLegendItems(eventsList, currentMonthDate);

                        return (
                            <section
                                key={`panel-${house._id}`}
                                id={`panel-${house._id}`}
                                className={`property-section property-panel ${house._id === resolvedActiveHouseId ? 'active' : ''}`}
                            >
                                <div className="property-header">
                                    <div className="property-info">
                                        <h3 className="property-name">{house.name}</h3>
                                        <div className="property-address">
                                            <i className="fas fa-map-marker-alt" />
                                            <span>
                                                {house.address.number} {house.address.road}, {house.address.zip} {house.address.city}
                                                {house.address.other ? ` - ${house.address.other}` : ''}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="property-calendar">
                                    <Calendar
                                        localizer={localizer}
                                        events={eventsList}
                                        startAccessor="start"
                                        endAccessor="end"
                                        views={['month']}
                                        popup
                                        popupOffset={12}
                                        style={{ height: calendarHeight }}
                                        eventPropGetter={(e) => ({
                                            className: e.type === "intervention"
                                                ? "intervention-calendar"
                                                : getBookingClassName(e)
                                        })}
                                        components={{
                                            toolbar: (toolbar) => (
                                                <CustomToolbar
                                                    toolbar={toolbar}
                                                    onBookClick={() => setSE({ addBooking: true, bookingHouseId: house._id })}
                                                    onMonthChange={(date) => {
                                                        setMonthCursorByHouse((prev) => ({
                                                            ...prev,
                                                            [house._id]: date
                                                        }));
                                                    }}
                                                />
                                            ),
                                            event: ({ event }) => <CalendarEvent event={event} />
                                        }}
                                        onSelectEvent={(event) => setSE(event.data)}
                                        messages={{
                                            today: "Aujourd'hui",
                                            previous: "Précédent",
                                            next: "Suivant",
                                            month: "Mois",
                                            week: "Semaine",
                                            day: "Jour",
                                            showMore: (total) => `+${total} plus`
                                        }}
                                    />

                                    {/* Calendar Legend */}
                                    {legendItems.length > 0 && (
                                        <div className="calendar-legend">
                                            {legendItems.map((item) => (
                                                <div className="legend-item" key={`${house._id}-${item.dot}`}>
                                                    <span className={`legend-dot ${item.dot}`} />
                                                    <span>{item.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Modals — only for active tab (avoids duplicate modals + wrong house) */}
                                {house._id === resolvedActiveHouseId
                                    ? renderModal(sE, setSE, house, () => {
                                        setSE({});
                                    }, (booking) => {
                                        handleBookingCreated(house._id, booking);
                                    })
                                    : null}
                            </section>
                        );
                    })
                ) : (
                    <div className="no-properties">
                        <div className="no-properties-icon">
                            <i className="fas fa-home" />
                        </div>
                        <h3>Aucune propriété</h3>
                        <p>
                            Votre propriété n'est pas encore enregistrée dans nos systèmes.
                            Ne vous en faites pas, elle devrait y être d'ici peu !
                        </p>
                    </div>
                )}
            </main>

        </div>
    );
}

function buildEventsList(house, optimistic = []) {
    const eventsList = [];

    house.interventions.forEach(intervention => {
        if (intervention.cancelled) return;

        const end = intervention.date.duration
            ? moment(intervention.date.dateStart).add(parseInt(intervention.date.duration.split(':')[0]), 'hours')
            : moment(intervention.date.dateEnd);

        eventsList.push({
            start: moment(intervention.date.dateStart).toDate(),
            end: end.toDate(),
            title: "Intervention",
            type: "intervention",
            data: intervention
        });
    });

    house.bookings.forEach(booking => {
        if (booking.cancelled) return;

        eventsList.push({
            start: moment(booking.dates.start).startOf('day').toDate(),
            end: moment(booking.dates.end).startOf('day').add(1, 'day').toDate(),
            title: titleName(booking.platform),
            type: "booking",
            allDay: true,
            data: booking
        });
    });

    optimistic.forEach((booking) => {
        eventsList.push({
            start: moment(booking.dates.start).startOf('day').toDate(),
            end: moment(booking.dates.end).startOf('day').add(1, 'day').toDate(),
            title: titleName(booking.platform),
            type: "booking",
            allDay: true,
            data: booking
        });
    });

    return eventsList.sort((a, b) => a.start - b.start);
}

function renderModal(selected, setSelected, house, closeModal, onBookingCreated) {
    if (selected?.date?.dateStart) {
        return (
            <Intervention
                data={selected}
                close={() => setSelected({})}
            />
        );
    }

    if (selected.platform) {
        return (
            <Event
                data={selected}
                close={() => setSelected({})}
            />
        );
    }
    if (selected.addBooking) {
        if (selected.bookingHouseId && selected.bookingHouseId !== house._id) {
            return null;
        }
        return (
            <Booking
                data={selected}
                house={house}
                close={closeModal}
                onBookingCreated={onBookingCreated}
            />
        );
    }
    return null;
}

function CustomToolbar({ toolbar, onBookClick, onMonthChange }) {
    const goToBack = () => {
        const newDate = new Date(
            toolbar.date.getFullYear(),
            toolbar.date.getMonth() - 1,
            1
        );
        toolbar.onNavigate('prev', newDate);
        onMonthChange(newDate);
    };

    const goToNext = () => {
        const newDate = new Date(
            toolbar.date.getFullYear(),
            toolbar.date.getMonth() + 1,
            1
        );
        toolbar.onNavigate('next', newDate);
        onMonthChange(newDate);
    };

    const label = moment(toolbar.date).format('MMMM YYYY');

    return (
        <div className="rbc-toolbar">
            <div className="calendar-month-nav">
                <button type="button" className="calendar-nav-btn" onClick={goToBack}>
                    <i className="fas fa-chevron-left" />
                </button>
                <span className="rbc-toolbar-label">{label}</span>
                <button type="button" className="calendar-nav-btn" onClick={goToNext}>
                    <i className="fas fa-chevron-right" />
                </button>
            </div>
            <div className="calendar-actions">
                <button type="button" className="calendar-book-btn" onClick={onBookClick}>
                    <i className="fas fa-plus" style={{ marginRight: '4px' }} />
                    <span className="d-none d-sm-inline">Réserver</span>
                </button>
            </div>
        </div>
    );
}

function CalendarEvent({ event }) {
    return (
        <span className={`calendar-event-chip ${event.type === "intervention" ? "calendar-event-chip--intervention" : ""}`}>
            {shortEventLabel(event.title)}
        </span>
    );
}

function shortEventLabel(title) {
    const value = (title || '').toLowerCase();
    if (value.includes('airbnb')) return 'Airbnb';
    if (value.includes('booking')) return 'Booking';
    if (value.includes('homeaway')) return 'HomeAway';
    if (value.includes('gites')) return 'Gites';
    if (value.includes('mypacome')) return 'MYPacome';
    if (value.includes('propriétaire') || value === 'back') return 'Propriétaire';
    if (value.includes('client') || value.includes('blocking')) return 'Blocage';
    if (value === 'unavailable' || value === 'block-unavailable') return 'Indisponibilité';
    if (value.includes('intervention')) return 'Intervention';
    return title || '';
}

function getBookingClassName(event) {
    const rawPlatform = (event?.data?.platform || '').toLowerCase();
    const rawTitle = (event?.title || '').toLowerCase();

    if (rawPlatform === 'back' || rawTitle.includes('propriétaire')) return 'block-back';
    if (rawPlatform === 'rent-client') return 'block-client';
    if (rawPlatform === 'unavailable' || rawPlatform === 'block-unavailable' || rawTitle.includes('unavailable')) return 'block-unavailable';
    if (rawPlatform.includes('airbnb') || rawTitle.includes('airbnb')) return 'block-airbnb';
    if (rawPlatform.includes('booking') || rawTitle.includes('booking')) return 'block-booking';
    if (rawPlatform.includes('homeaway') || rawTitle.includes('homeaway')) return 'block-ha';
    if (rawPlatform.includes('ical-gites') || rawTitle.includes('gites')) return 'block-gites';
    if (rawPlatform.includes('planning manager') || rawTitle.includes('mypacome')) return 'block-pm';
    if (rawPlatform.includes('blocage client') || rawTitle.includes('blocage client') || rawTitle.includes('client')) return 'block-client';

    return classNameBlocking(event.title);
}

function getCalendarHeight(events, monthDate) {
    const weeksInMonth = getWeeksInMonth(monthDate);
    const dayCounts = new Map();

    events.forEach((event) => {
        const start = moment(event.start).startOf('day');
        const rangeEnd = moment(event.end).startOf('day');
        if (!rangeEnd.isAfter(start)) {
            const key = start.format('YYYY-MM-DD');
            dayCounts.set(key, (dayCounts.get(key) || 0) + 1);
            return;
        }
        let cursor = start.clone();
        while (cursor.isBefore(rangeEnd)) {
            const key = cursor.format('YYYY-MM-DD');
            dayCounts.set(key, (dayCounts.get(key) || 0) + 1);
            cursor.add(1, 'day');
        }
    });

    const maxDailyEvents = dayCounts.size ? Math.max(...dayCounts.values()) : 0;
    const baseHeight = 152;
    const perWeekMin = 92;
    const weekBlockHeight = weeksInMonth * perWeekMin;
    const stackedExtra = Math.max(0, maxDailyEvents - 1) * 30;

    return Math.max(540, Math.min(1200, baseHeight + weekBlockHeight + stackedExtra));
}

function getWeeksInMonth(monthDate) {
    const start = moment(monthDate).startOf('month').startOf('week');
    const end = moment(monthDate).endOf('month').endOf('week');
    return end.diff(start, 'weeks') + 1;
}

function getVisibleLegendItems(events, monthDate) {
    const monthStart = moment(monthDate).startOf('month');
    const monthEnd = moment(monthDate).endOf('month');
    const visibleTypes = new Set();

    events.forEach((event) => {
        const eventStart = moment(event.start);
        const eventEnd = moment(event.end);
        const intersectsMonth = eventStart.isSameOrBefore(monthEnd, 'day') && eventEnd.isSameOrAfter(monthStart, 'day');

        if (!intersectsMonth) {
            return;
        }

        if (event.type === 'intervention') {
            visibleTypes.add('intervention');
            return;
        }

        const className = getBookingClassName(event);
        switch (className) {
            case 'block-airbnb':
                visibleTypes.add('airbnb');
                break;
            case 'block-booking':
                visibleTypes.add('booking');
                break;
            case 'block-ha':
                visibleTypes.add('homeaway');
                break;
            case 'block-client':
                visibleTypes.add('client');
                break;
            case 'block-back':
                visibleTypes.add('back');
                break;
            case 'block-pm':
                visibleTypes.add('mypacome');
                break;
            case 'block-gites':
                visibleTypes.add('gites');
                break;
            case 'block-unavailable':
                visibleTypes.add('unavailable');
                break;
            default:
                break;
        }
    });

    const order = ['intervention', 'airbnb', 'booking', 'homeaway', 'client', 'back', 'mypacome', 'gites', 'unavailable'];
    const labels = {
        intervention: 'Intervention',
        airbnb: 'Airbnb',
        booking: 'Booking',
        homeaway: 'HomeAway',
        client: 'Blocage client',
        back: 'Blocage propriétaire',
        mypacome: 'MYPacôme',
        gites: 'Gites de France',
        unavailable: 'Indisponibilité'
    };

    return order
        .filter((type) => visibleTypes.has(type))
        .map((type) => ({ dot: type, label: labels[type] }));
}
